import * as Tone from 'tone';
import { audioBufferToWav } from './wav-encoder.js';
import { Metronome } from './metronome.js';
import { debugLog, flushDebugLogNow } from './debug-log.js';

/** URL of the AudioWorkletProcessor script — served from /public. */
const WORKLET_URL = '/recorder-worklet.js';

/** DAW-style transport state. `idle` = stopped at playhead, others = transport running. */
export type TransportState = 'idle' | 'playing' | 'recording';

export interface Track {
  id: string;
  name: string;
  buffer: AudioBuffer;
  durationMs: number;
  gain: number;
  pan: number;
  muted: boolean;
  /**
   * Latency-compensation fine-tune, ms (clamped ±2000).
   *   > 0 — delays the track by N ms (plays later)
   *   < 0 — trims N ms from the start (plays earlier)
   *   = 0 — plays unmodified relative to its song-position
   */
  offsetMs: number;
  /**
   * Song-position in ms (>= 0). 0 means "starts at the top of the sketch".
   * This is the new transport-axis position set by recording at a playhead.
   */
  startAtMs: number;
  /**
   * The raw encoded recording (webm/opus/mp4) for newly-recorded tracks.
   * Imported tracks (from the server) don't have this — they only carry
   * the decoded buffer. We need this to upload the original stem to the
   * server rather than re-mixing it.
   */
  originalBlob: Blob | null;
  /**
   * Pre-computed peak data for the waveform display — normalised max-abs
   * values per bucket [0..1]. Computed once when the track is added.
   */
  peaks: number[];
  /**
   * When set, this track is the live recording target. `recordingMs` reflects
   * the running capture duration. The track's `buffer` is a placeholder
   * 1-sample silence; consumers should use `recordingMs` for layout while
   * recording is in progress. Cleared in `stopRecording` when the real
   * buffer replaces the placeholder.
   */
  recordingMs?: number;
  /**
   * Single-arm invariant: at most one track has `armed: true`. The global
   * record button records onto whichever track is armed. An empty
   * (`durationMs === 0`) armed track is the natural target for the next take.
   */
  armed: boolean;
}

/** A track is "empty" when it has no audio yet and isn't currently recording. */
export function isEmptyTrack(t: Track): boolean {
  return t.durationMs === 0 && t.recordingMs === undefined;
}

/** Number of buckets for the waveform peak summary. Two pixels per bucket
 * @120px-wide displays gives reasonable detail without bloating the UI. */
export const WAVEFORM_BUCKETS = 60;

/**
 * Reduce an AudioBuffer to N peak values for waveform rendering.
 * Each bucket gets the maximum absolute sample value in its window.
 * Returns values in the range [0, 1].
 */
export function computePeaks(buffer: AudioBuffer, numBuckets: number = WAVEFORM_BUCKETS): number[] {
  const data = buffer.getChannelData(0);
  if (data.length === 0) return new Array(numBuckets).fill(0);
  const samplesPerBucket = Math.max(1, Math.floor(data.length / numBuckets));
  const peaks: number[] = new Array(numBuckets).fill(0);
  let maxOverall = 0;
  for (let b = 0; b < numBuckets; b++) {
    let m = 0;
    const start = b * samplesPerBucket;
    const end = b === numBuckets - 1 ? data.length : Math.min(start + samplesPerBucket, data.length);
    for (let i = start; i < end; i++) {
      const v = Math.abs(data[i]);
      if (v > m) m = v;
    }
    peaks[b] = m;
    if (m > maxOverall) maxOverall = m;
  }
  // Normalise so the loudest bucket reads as 1.0 — makes quiet tracks visible.
  if (maxOverall > 0) {
    for (let b = 0; b < numBuckets; b++) peaks[b] /= maxOverall;
  }
  return peaks;
}

/**
 * Capture pipeline configuration.
 *
 * Two routes:
 *
 * 1. Built-in mic (preferred). Full bandwidth (~48 kHz native), no codec
 *    compression. Used for any source where fidelity matters — guitars,
 *    instruments, anything with harmonic content above ~7 kHz. We pick
 *    it explicitly via `deviceId` even when AirPods are connected, so
 *    playback can stay on AirPods while capture stays clean.
 *
 * 2. Bluetooth fallback (HFP). If no non-BT input is enumerable we
 *    fall back to requesting `sampleRate: { exact: 16000 }`, which is
 *    iOS's HFP native rate — the only setting that avoids the WebKit
 *    resampler bug (#221334) on AirPods capture. Sounds fine on voice
 *    because the mSBC voice codec sits in the speech band; instruments
 *    sound degraded because the codec strips harmonics above ~7 kHz
 *    and applies voice-tuned processing. That degradation is a BT
 *    protocol limit, not something we can fix from JS.
 */
const BT_MIC_LABEL_RX = /airpods|bluetooth|hands[- ]?free|beats|bose|sony wf|wireless/i;

const BT_FALLBACK_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  sampleRate: { exact: 16000 },
};

const BUILT_IN_BASE_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

/** Cached deviceId for the built-in mic once we've found it. Persists
 *  for the page lifetime — device identity is stable. */
let _cachedBuiltInDeviceId: string | null = null;

/** Pick the best available mic for instrument-quality capture.
 *
 *  Strategy: enumerate audio inputs, find one whose label doesn't match
 *  Bluetooth heuristics, return constraints that target it via deviceId
 *  (without an `exact` sampleRate so iOS delivers native ~48 kHz).
 *
 *  Falls back to the 16 kHz BT-safe constraints if no non-BT mic can be
 *  identified — e.g. labels are empty (permission not yet granted and
 *  no priming call made) or only AirPods are enumerable.
 *
 *  iOS WebKit returns empty labels until any mic permission has been
 *  granted in the page. We deliberately do NOT do a priming
 *  getUserMedia here; the very first record press will fall through to
 *  the BT-safe path, populate labels, cache the built-in deviceId, and
 *  use it on every subsequent record. */
async function pickMicConstraints(): Promise<MediaTrackConstraints> {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return BT_FALLBACK_CONSTRAINTS;
    let devices = await navigator.mediaDevices.enumerateDevices();
    let audioInputs = devices.filter((d) => d.kind === 'audioinput');

    // Cached deviceId path: skip re-scanning labels entirely.
    if (_cachedBuiltInDeviceId) {
      const stillThere = audioInputs.some((d) => d.deviceId === _cachedBuiltInDeviceId);
      if (stillThere) {
        return { ...BUILT_IN_BASE_CONSTRAINTS, deviceId: { exact: _cachedBuiltInDeviceId } };
      }
      _cachedBuiltInDeviceId = null;
    }

    // iOS quirk: device labels are empty until ANY getUserMedia has
    // succeeded in the page session. On the very first record press
    // we therefore have no way to identify the built-in mic by label,
    // and fall back to BT_FALLBACK_CONSTRAINTS (sampleRate exact 16000)
    // — which on iPhone-Mikrofon engages a much more aggressive AGC
    // than the unconstrained-rate path, dragging sustained tones down
    // 24× over ~4 s. Server-log measurement, 2026-05-12.
    //
    // Workaround: if labels are empty, do a throwaway getUserMedia
    // with no constraints purely to grant permission, then re-
    // enumerate so we can pick the built-in mic by label on the same
    // record press. Costs ~1 s extra on the FIRST take only.
    if (audioInputs.length > 0 && audioInputs.every((d) => !d.label)) {
      try {
        const prime = await navigator.mediaDevices.getUserMedia({ audio: true });
        for (const t of prime.getTracks()) t.stop();
        // Let iOS release the audio session before the real gum.
        await new Promise<void>((r) => setTimeout(r, 100));
        devices = await navigator.mediaDevices.enumerateDevices();
        audioInputs = devices.filter((d) => d.kind === 'audioinput');
      } catch (e) {
        console.warn('mic picker: priming gum failed:', e);
        return BT_FALLBACK_CONSTRAINTS;
      }
    }

    // Find first device with a populated label that isn't a BT mic.
    const builtIn = audioInputs.find((d) => d.label && !BT_MIC_LABEL_RX.test(d.label));
    if (!builtIn) {
      console.log('mic picker: no non-BT input found, falling back to BT-safe 16 kHz');
      return BT_FALLBACK_CONSTRAINTS;
    }

    _cachedBuiltInDeviceId = builtIn.deviceId;
    console.log('mic picker: chose built-in:', builtIn.label);
    return { ...BUILT_IN_BASE_CONSTRAINTS, deviceId: { exact: builtIn.deviceId } };
  } catch (e) {
    console.warn('mic picker failed:', e);
    return BT_FALLBACK_CONSTRAINTS;
  }
}

/** No warmup needed at 16 kHz — the resampler-renegotiation issue that
 *  the warmup was meant to paper over doesn't apply when we never
 *  resample. */
const WARMUP_SEC = 0;

/** Snapshot of MediaStreamTrack.getSettings() at mic open. iOS fills
 *  very few of these fields on a typical recording — we keep what's
 *  there for debugging when something goes wrong. */
export interface MicOpenInfo {
  deviceId: string | null;
  label: string | null;
  groupId: string | null;
  sampleRate: number | null;
  channelCount: number | null;
  latencySec: number | null;
  echoCancellation: boolean | null;
  noiseSuppression: boolean | null;
  autoGainControl: boolean | null;
}

/**
 * Per-track playback wrapper. Lazily created on first play/record from a
 * Track's AudioBuffer. The Channel owns volume (in dB), pan (-1..1), and
 * mute state — `setTrackGain` etc. mutate the Channel and Tone handles
 * the smooth ramp internally.
 *
 * Connection: player → channel → Tone.Destination (idle) OR an
 * MediaStreamAudioDestinationNode (recording, iOS routing).
 */
interface TrackPlayer {
  player: Tone.Player;
  channel: Tone.Channel;
}

/** Convert linear gain (0..2, 1.0 = unity) to dB for Tone.Channel.volume. */
function gainToDb(linear: number): number {
  if (linear <= 0.0001) return -60;
  return 20 * Math.log10(linear);
}

/**
 * Force `usedtx=0` on the Opus codec line in an SDP blob. Opus DTX
 * (discontinuous transmission) drops packets when input is below ~-55
 * dBFS, which makes WebRTC NetEq's jitter buffer time-compress when
 * real audio resumes — audible as a brief pitch-up on track attacks
 * during overdub monitoring. Disabling DTX keeps the codec
 * continuously transmitting so the buffer stays at steady state.
 *
 * Safe to call with any SDP — returns input unchanged if no Opus
 * payload type is present.
 */
function patchOpusUsedtxOff(sdp: string): string {
  const opusMatch = sdp.match(/^a=rtpmap:(\d+)\s+opus\//im);
  if (!opusMatch) return sdp;
  const pt = opusMatch[1];
  const fmtpRegex = new RegExp(`^a=fmtp:${pt}\\s+(.*)$`, 'im');
  if (fmtpRegex.test(sdp)) {
    return sdp.replace(fmtpRegex, (_match, params: string) => {
      const filtered = params
        .split(';')
        .map((p) => p.trim())
        .filter((p) => p.length > 0 && !p.startsWith('usedtx='));
      filtered.push('usedtx=0');
      return `a=fmtp:${pt} ${filtered.join(';')}`;
    });
  }
  // No existing fmtp line for this payload type — insert one right
  // after the rtpmap.
  return sdp.replace(opusMatch[0], `${opusMatch[0]}\r\na=fmtp:${pt} usedtx=0`);
}

/**
 * Given a track and the current playhead (in Transport seconds), compute
 * where on the Transport timeline this track's Player should start and how
 * far into its buffer to seek. Returns null if the clip is entirely behind
 * the playhead (nothing to schedule).
 *
 * trackStartSec = (startAtMs + offsetMs) / 1000 — can be negative if offset
 * pulls the clip before song-start. We clamp the player-start to the
 * playhead and seek the corresponding amount into the buffer.
 */
export function computeStartInTransport(
  t: Pick<Track, 'startAtMs' | 'offsetMs' | 'buffer'>,
  playheadSec: number
): { transportTimeSec: number; bufferOffsetSec: number } | null {
  const trackStartSec = (t.startAtMs + t.offsetMs) / 1000;
  const playerStartInTransport = Math.max(trackStartSec, playheadSec);
  const bufferOffsetSec = playerStartInTransport - trackStartSec;
  if (bufferOffsetSec >= t.buffer.duration) return null;
  return { transportTimeSec: playerStartInTransport, bufferOffsetSec };
}

/**
 * Multi-track audio engine. Holds an AudioContext, a list of decoded tracks,
 * and orchestrates record + playback + mixdown.
 *
 * Recording is tap-to-start / tap-to-stop, no max duration.
 * During recording, all unmuted existing tracks play back so the user can
 * overdub. (Headphones mandatory — speakers cause feedback.)
 */
export class AudioEngine {
  // Not `readonly` because softReset() rebuilds them — see softReset() docs.
  // External users access via `engine.ctx` / `engine.metronome` at each
  // call site; nobody captures a long-lived reference, so swapping these
  // out under the hood is safe.
  ctx: AudioContext;
  readonly tracks: Track[] = [];
  metronome: Metronome;

  // Sample-accurate capture via AudioWorklet (no MediaRecorder).
  private workletLoaded = false;
  private workletNode: AudioWorkletNode | null = null;
  private streamSource: MediaStreamAudioSourceNode | null = null;
  private capturedChunks: Float32Array[] = [];
  private recording = false;
  private workletStopResolve: (() => void) | null = null;
  private mediaStream: MediaStream | null = null;
  private recordingStartedAt = 0;
  // ctx.sampleRate snapshot taken when capture begins. If iOS shifts the
  // context's sample rate mid-recording (BT route/codec switch), the
  // worklet samples are paced at the START rate, not the STOP rate.
  // We use this value when constructing the final AudioBuffer so the
  // declared rate matches the actual capture pacing — keeps pitch correct
  // even if iOS quietly renegotiates underneath us.
  private captureSampleRate = 0;

  // iOS keep-alive: two independent mechanisms to keep the audio session
  // in Playback mode after getUserMedia ends.
  //
  // 1. A hidden <audio> element looping a silent WAV — this is what
  //    actually convinces iOS WebKit to use the Playback session category
  //    (instead of Ambient, which gets muted by the silent switch and can
  //    silence Web Audio output entirely after a record cycle).
  // 2. A silent BufferSource inside the AudioContext — belt-and-suspenders.
  private keepAliveAudioEl: HTMLAudioElement | null = null;
  private keepAliveAudioUrl: string | null = null;
  private keepAliveVisibilityHandler: (() => void) | null = null;
  private keepAlive: AudioBufferSourceNode | null = null;
  // Timer for the delayed keep-alive teardown after recording. We hold
  // keep-alive a few seconds past stopRecording so iOS doesn't mute Web
  // Audio mid-transition, then release it so AirPods can switch back
  // from HFP (mono ~16 kHz, phone-call quality) to A2DP (music quality).
  // Cleared if the user starts recording/playback again before it fires.
  private keepAliveReleaseTimer: ReturnType<typeof setTimeout> | null = null;

  // During recording, iOS forces the page-wide audio session into
  // PlayAndRecord, which routes both AudioContext.destination AND plain
  // <audio> elements through the earpiece. The fix: pipe the backing-track
  // graph through a WebRTC loopback (a pair of RTCPeerConnections) into
  // the consuming <audio> element. iOS classifies WebRTC tracks as voice
  // /video chat, which defaults to the speakerphone (main loudspeaker)
  // in PlayAndRecord — bypassing the earpiece routing.
  private recordOutDest: MediaStreamAudioDestinationNode | null = null;
  private recordOutAudio: HTMLMediaElement | null = null;
  private recordOutPc1: RTCPeerConnection | null = null;
  private recordOutPc2: RTCPeerConnection | null = null;
  // Low-amplitude tone kept flowing into recordOutDest for the entire
  // recording lifetime. WebRTC's NetEq jitter buffer applies time-
  // compression (= pitch up) to catch up when its buffer fills faster
  // than it drains; if the dest goes silent and then real audio appears
  // (backing-track attack), NetEq jumps and we hear 1-3 s of pitch
  // shift on monitoring. A 220 Hz sine sits above WebRTC's send-side
  // high-pass filter (~80 Hz) and above Opus's VAD threshold, so the
  // stream is always "active" and NetEq stays at steady state.
  //
  // Audible level: amplitude 5e-4 = ~-66 dBFS. Faint hum at very high
  // monitoring volume; inaudible at normal levels.
  private recordOutKeepAlive: OscillatorNode | null = null;
  private recordOutKeepAliveGain: GainNode | null = null;

  // Callback fired when playback ends naturally OR via manual stopPlayback.
  // Set by play(), cleared by stopPlayback().
  private playbackOnEnd: (() => void) | null = null;

  // Transport (DAW-style): a single playhead and state observer.
  private _playheadMs = 0;
  private _playheadStartedAt: number | null = null;
  private _playheadListeners = new Set<(ms: number, state: TransportState) => void>();
  private _recordedStartAtMs = 0;
  private _recordingTrackId: string | null = null;

  // Single-arm invariant: at most one track is armed at a time. The arm
  // designates the target of the next `startRecording()` call. Empty armed
  // tracks (no audio yet) are common — created by "+" in the UI.
  private _armedTrackId: string | null = null;

  // Last capture's sample-rate diagnostic. Populated in stopRecording.
  // Exposed so the UI can flag pitch-warp conditions (BT codec switch
  // mid-take) where ctx.sampleRate ≠ totalSamples / elapsedSec.
  lastCaptureDiagnostic: {
    ctxRate: number;
    startRate: number;
    effectiveRate: number;
    streamRate: number | string;
    samples: number;
    elapsedSec: string;
    streamLabel: string | null;
    streamChannelCount: number | null;
  } | null = null;

  // Snapshot of what iOS actually gave us at mic open. Kept for
  // debugging when something goes wrong, but no longer surfaced in
  // production UI — the diagnostic mode picker that consumed this is
  // gone now that we're locked to 16 kHz exact.
  lastMicOpenInfo: MicOpenInfo | null = null;

  // Listeners fired the instant getUserMedia resolves and we've snapshotted
  // the stream settings. Kept on the engine in case future debugging
  // needs to hook in; production main.ts no longer subscribes.
  private _micOpenListeners = new Set<(info: MicOpenInfo) => void>();

  // Per-track Tone.Player + Tone.Channel wrappers, keyed by Track.id.
  // Lazily created on first play; disposed on track removal. Empty tracks
  // (no audio yet) have no entry until they're filled by a recording.
  private trackPlayers = new Map<string, TrackPlayer>();

  // Single "output bus" that all per-track Channels connect to. We then
  // re-route this bus between Tone.Destination (idle / playback) and the
  // WebRTC loopback's MediaStreamAudioDestinationNode (recording). This
  // is more reliable than disconnect/reconnecting each Channel
  // individually — Tone.Channel's output graph can be finicky to
  // re-wire on the fly.
  private outputBus: Tone.Gain | null = null;

  // True while standalone playback (play()) is active. Independent of the
  // recording flag — recording uses a different scheduling path. Set/cleared
  // by play() / stopPlayback().
  private _playbackActive = false;
  // Tone.Transport.scheduleOnce id for the auto-stop-when-longest-track-ends
  // event. Cancelled on manual stopPlayback / seek-while-playing.
  private _playbackEndEventId: number | null = null;
  // Synced players for the current playback session. Tracked so we can
  // unsync/stop them cleanly on stopPlayback / seek.
  private _syncedPlayers: Tone.Player[] = [];

  /**
   * Latency compensation in milliseconds. When > 0, the first N ms of each
   * recorded track are trimmed before being stored. This corrects for the
   * round-trip delay (mic-in → headphone-out → ear → mic) that causes
   * overdubs to land late. Manually adjusted by the user via the UI.
   */
  private latencyOffsetMs = 0;

  /**
   * When true, recording is preceded by 4 audible click beats (not captured).
   * Existing tracks' playback is also delayed so they start on the 5th beat,
   * keeping the new overdub aligned with the click.
   */
  private countInEnabled = false;
  private countingIn = false;

  /**
   * When true, the metronome runs continuously from `recorderStartTime`
   * through the take, not just the 4-beat count-in. Clicks go through
   * `ctx.destination` so on iOS they share the PlayAndRecord routing
   * (earpiece without headphones). Use with headphones for clean takes.
   */
  private metronomeDuringRec = false;

  /**
   * Multiplier applied to existing-track playback during overdub, on top of
   * each track's own gain. Compensates for iOS PlayAndRecord routing
   * audio to the small earpiece speaker while the mic is active — pushing
   * the signal toward the earpiece's max output. Has no effect when
   * headphones are connected (their own routing path is loud already);
   * the toggle in the UI lets the user disable it when not needed.
   * 1.0 = no boost. Set 4.0 on iOS by default.
   */
  private overdubBoost = 1.0;

  constructor() {
    // Create a NATIVE AudioContext first, then hand it to Tone via
    // Tone.setContext(). Without this, Tone constructs the context via
    // `standardized-audio-context` (its polyfill), which produces a
    // wrapper that fails `instanceof BaseAudioContext` checks — and our
    // custom AudioWorkletNode constructor uses exactly that check.
    //
    // By sharing a native context, both worlds work:
    //   - new AudioWorkletNode(this.ctx, ...) — uses native context
    //   - Tone.Player / Tone.Channel / Tone.Transport — use same context
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    // Default AudioContext options. We don't pass `latencyHint:
    // 'playback'` anymore — that was a workaround for buffer underruns
    // on the 48 kHz BT path, which doesn't apply at 16 kHz capture.
    this.ctx = new Ctor();
    Tone.setContext(this.ctx);
    this.metronome = new Metronome(this.ctx);
    // Build the output bus AFTER Tone.setContext so it lives on the
    // shared native context. Always-on; routed to Tone.Destination by
    // default and switched to recordOutNode while recording.
    this.outputBus = new Tone.Gain(1);
    this.outputBus.toDestination();
  }

  /**
   * Switch the global output bus between Tone.Destination (idle / play)
   * and a recording-time MediaStreamAudioDestinationNode (so its stream
   * feeds the WebRTC loopback that bypasses iOS earpiece routing).
   */
  private routeOutputBus(dest: AudioNode | null): void {
    if (!this.outputBus) return;
    try { this.outputBus.disconnect(); } catch { /* ignore */ }
    if (dest === null) {
      this.outputBus.toDestination();
    } else {
      // Tone.Gain.connect accepts a native AudioNode.
      this.outputBus.connect(dest);
    }
  }

  getLatencyOffsetMs(): number {
    return this.latencyOffsetMs;
  }
  setLatencyOffsetMs(ms: number): void {
    this.latencyOffsetMs = Math.max(0, Math.min(1000, Math.round(ms)));
  }

  getCountInEnabled(): boolean {
    return this.countInEnabled;
  }
  setCountInEnabled(on: boolean): void {
    this.countInEnabled = on;
  }
  get isCountingIn(): boolean {
    return this.countingIn;
  }

  getOverdubBoost(): number {
    return this.overdubBoost;
  }
  setOverdubBoost(boost: number): void {
    this.overdubBoost = Math.max(1, Math.min(10, boost));
  }

  getMetronomeDuringRecording(): boolean {
    return this.metronomeDuringRec;
  }
  setMetronomeDuringRecording(on: boolean): void {
    this.metronomeDuringRec = on;
  }

  /** iOS / mobile webviews start AudioContext suspended; user gesture resumes.
   *
   * We call resume() unconditionally here (not only when state==='suspended')
   * because after a getUserMedia → record cycle, iOS WebKit can leave the
   * AudioContext in a half-state where it reports 'running' but the
   * destination is muted. A fresh resume() inside a user-gesture-derived
   * call kicks it back to life. Cheap when already running.
   *
   * Also lazily starts the iOS keep-alive source on first call so the
   * audio session stays in playback mode after recording ends.
   */
  async ensureRunning(): Promise<void> {
    // Cancel any pending keep-alive release — the user is taking another
    // audio action, so we want the session active again immediately.
    this.cancelKeepAliveRelease();
    // Tone.start() unlocks the shared context on first user gesture.
    // Idempotent on subsequent calls. We still call ctx.resume() too:
    // after a record cycle iOS sometimes reports state==='running' while
    // the destination is muted — a manual resume() inside a gesture-
    // derived call kicks it back to life.
    try {
      await Tone.start();
    } catch {
      /* ignore — likely not in a user gesture; ctx.resume() will retry */
    }
    try {
      await this.ctx.resume();
    } catch {
      /* ignore — best-effort */
    }
    this.ensureKeepAlive();
    // Resume the silent BufferSource keep-alive too if a prior release
    // stopped it (releaseKeepAlive sets this.keepAlive = null).
    if (this.keepAliveAudioEl && this.keepAliveAudioEl.paused) {
      const p = this.keepAliveAudioEl.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => { /* ignore */ });
      }
    }
  }

  /**
   * Start the two keep-alive mechanisms so iOS WebKit keeps the audio
   * session in Playback mode after getUserMedia ends.
   *
   * This must be called from within a user gesture (e.g. click handler)
   * to satisfy iOS's autoplay rules — ensureRunning() is invoked from
   * startRecording() / play() / etc., which themselves are click-driven.
   */
  private ensureKeepAlive(): void {
    // 1) BufferSource keep-alive inside the AudioContext
    if (!this.keepAlive) {
      try {
        const buf = this.ctx.createBuffer(1, this.ctx.sampleRate, this.ctx.sampleRate);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        const gain = this.ctx.createGain();
        gain.gain.value = 0;
        src.connect(gain).connect(this.ctx.destination);
        src.start();
        this.keepAlive = src;
      } catch (e) {
        console.warn('AudioContext keep-alive failed:', e);
      }
    }

    // 2) <audio> element looping silent WAV — THIS is what shifts iOS
    //    into the Playback session category. Without this, our Web Audio
    //    output goes silent after recording on iOS.
    if (!this.keepAliveAudioEl) {
      try {
        const silentBuf = this.ctx.createBuffer(1, Math.round(this.ctx.sampleRate * 0.5), this.ctx.sampleRate);
        const wavBlob = audioBufferToWav(silentBuf);
        const url = URL.createObjectURL(wavBlob);
        const el = document.createElement('audio');
        el.src = url;
        el.loop = true;
        el.preload = 'auto';
        el.volume = 0.001; // not muted — iOS still counts this as "playing"
        el.setAttribute('playsinline', '');
        el.setAttribute('webkit-playsinline', '');
        el.style.position = 'absolute';
        el.style.width = '1px';
        el.style.height = '1px';
        el.style.opacity = '0';
        el.style.pointerEvents = 'none';
        document.body.appendChild(el);
        const playPromise = el.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch((e) => {
            console.warn('<audio> keep-alive play() rejected:', e);
          });
        }
        this.keepAliveAudioEl = el;
        this.keepAliveAudioUrl = url;

        this.keepAliveVisibilityHandler = () => {
          if (document.visibilityState === 'visible' && this.keepAliveAudioEl?.paused) {
            const p = this.keepAliveAudioEl.play();
            if (p && typeof p.then === 'function') {
              p.catch((err) => {
                console.warn('<audio> keep-alive resume rejected:', err);
              });
            }
          }
        };
        document.addEventListener('visibilitychange', this.keepAliveVisibilityHandler);
      } catch (e) {
        console.warn('<audio> keep-alive failed:', e);
      }
    }
  }

  /**
   * Pause (but don't destroy) the keep-alive resources so iOS can release
   * the audio session and let connected Bluetooth headphones return to
   * their high-quality output profile (A2DP). Call after a recording ends.
   *
   * Re-enable by calling `ensureKeepAlive()` again on the next user
   * gesture (record/play/+) — see refreshKeepAlive(), wired via the
   * click handlers in main.ts.
   */
  private releaseKeepAlive(): void {
    if (this.keepAliveAudioEl) {
      try { this.keepAliveAudioEl.pause(); } catch { /* ignore */ }
    }
    if (this.keepAlive) {
      try { this.keepAlive.stop(); } catch { /* ignore */ }
      this.keepAlive = null;
    }
    // Suspend the AudioContext too — without an active stream, iOS will
    // promptly drop the PlayAndRecord category. Resume on next gesture.
    if (this.ctx.state === 'running') {
      this.ctx.suspend().catch(() => { /* ignore */ });
    }
  }

  /**
   * Cancel a pending keep-alive release. Called when the user starts a
   * new audio action before the release fired.
   */
  cancelKeepAliveRelease(): void {
    if (this.keepAliveReleaseTimer) {
      clearTimeout(this.keepAliveReleaseTimer);
      this.keepAliveReleaseTimer = null;
    }
  }

  /**
   * Schedule a delayed keep-alive release. Call after stopRecording /
   * cancelCountIn so iOS gets a few seconds to settle before we let the
   * audio session lapse. Re-callable — resets the timer.
   */
  scheduleKeepAliveRelease(delayMs = 3000): void {
    this.cancelKeepAliveRelease();
    this.keepAliveReleaseTimer = setTimeout(() => {
      this.keepAliveReleaseTimer = null;
      this.releaseKeepAlive();
    }, delayMs);
  }

  /**
   * Tear down the iOS keep-alive resources. Safe to call multiple times.
   * Should be invoked on `pagehide` / `beforeunload` so the blob URL doesn't
   * leak across page reloads.
   */
  dispose(): void {
    this.cancelKeepAliveRelease();
    if (this.keepAliveVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.keepAliveVisibilityHandler);
      this.keepAliveVisibilityHandler = null;
    }
    if (this.keepAliveAudioEl) {
      try {
        this.keepAliveAudioEl.pause();
        this.keepAliveAudioEl.removeAttribute('src');
        this.keepAliveAudioEl.load();
        this.keepAliveAudioEl.remove();
      } catch {
        /* ignore */
      }
      this.keepAliveAudioEl = null;
    }
    if (this.keepAliveAudioUrl) {
      URL.revokeObjectURL(this.keepAliveAudioUrl);
      this.keepAliveAudioUrl = null;
    }
    if (this.keepAlive) {
      try {
        this.keepAlive.stop();
      } catch {
        /* ignore */
      }
      this.keepAlive = null;
    }
  }

  /**
   * Soft reset: close the current AudioContext and rebuild a fresh one with
   * the same MicMode config. Preserves track data (re-creates AudioBuffers
   * in the new context from snapshotted Float32 samples) and listener
   * subscriptions. Does NOT cause a page reload.
   *
   * Hypothesis being tested: iOS holds an audio-session route decision for
   * the WebView's lifetime. A full page reload resets that decision; this
   * method tries to do the same thing in-place by closing the context
   * (releasing iOS's audio session reference) and waiting briefly before
   * opening a fresh one — letting iOS recompute the route, ideally back to
   * A2DP-out + builtin-mic for AirPods.
   */
  async softReset(): Promise<void> {
    // 1) Halt anything in flight
    if (this.isRecording || this.isCountingIn) {
      try { this.cancelCountIn(); } catch { /* ignore */ }
    }
    if (this.isPlaying) {
      try { this.stopPlayback(); } catch { /* ignore */ }
    }

    // 2) Snapshot track contents as raw Float32 + metadata. AudioBuffers
    //    are bound to the old context — we'll rebuild fresh ones in the
    //    new context after rehydration.
    interface TrackSnapshot {
      id: string;
      name: string;
      samples: Float32Array;
      sampleRate: number;
      durationMs: number;
      gain: number;
      pan: number;
      muted: boolean;
      offsetMs: number;
      startAtMs: number;
      originalBlob: Blob | null;
      peaks: number[];
      armed: boolean;
    }
    const snapshots: TrackSnapshot[] = this.tracks
      .filter((t) => t.buffer.length > 1) // skip 1-sample placeholders
      .map((t) => ({
        id: t.id,
        name: t.name,
        samples: t.buffer.getChannelData(0).slice(),
        sampleRate: t.buffer.sampleRate,
        durationMs: t.durationMs,
        gain: t.gain,
        pan: t.pan,
        muted: t.muted,
        offsetMs: t.offsetMs,
        startAtMs: t.startAtMs,
        originalBlob: t.originalBlob,
        peaks: t.peaks.slice(),
        armed: t.armed,
      }));

    // 3) Tear down per-track Tone graph
    for (const tp of this.trackPlayers.values()) {
      try { tp.player.dispose(); } catch { /* ignore */ }
      try { tp.channel.dispose(); } catch { /* ignore */ }
    }
    this.trackPlayers.clear();

    // 4) Tear down output bus
    if (this.outputBus) {
      try { this.outputBus.dispose(); } catch { /* ignore */ }
      this.outputBus = null;
    }

    // 5) Stop metronome
    try { this.metronome.stop(); } catch { /* ignore */ }

    // 6) Release media stream + recording graph
    if (this.mediaStream) {
      for (const t of this.mediaStream.getTracks()) {
        try { t.stop(); } catch { /* ignore */ }
      }
      this.mediaStream = null;
    }
    this.streamSource = null;
    this.workletNode = null;
    this.workletLoaded = false;
    this.capturedChunks = [];
    this.teardownRecordOutput();

    // 7) Tear down keep-alive + close context
    this.dispose();
    try {
      await this.ctx.close();
    } catch {
      /* ignore — may already be closed */
    }

    // 8) Brief delay — gives iOS a chance to fully release the audio
    //    session before we ask it for a new one. 250 ms is empirically
    //    enough for most BT route resets; longer doesn't seem to help.
    await new Promise((r) => setTimeout(r, 250));

    // 9) Rebuild context with defaults — same as the engine constructor.
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctor();
    Tone.setContext(this.ctx);

    // 10) Rebuild metronome + output bus on the new context
    this.metronome = new Metronome(this.ctx);
    this.outputBus = new Tone.Gain(1);
    this.outputBus.toDestination();

    // 11) Rehydrate tracks — create new AudioBuffers on the new context
    this.tracks.length = 0;
    for (const snap of snapshots) {
      const buf = this.ctx.createBuffer(1, snap.samples.length, snap.sampleRate);
      buf.getChannelData(0).set(snap.samples);
      this.tracks.push({
        id: snap.id,
        name: snap.name,
        buffer: buf,
        durationMs: snap.durationMs,
        gain: snap.gain,
        pan: snap.pan,
        muted: snap.muted,
        offsetMs: snap.offsetMs,
        startAtMs: snap.startAtMs,
        originalBlob: snap.originalBlob,
        peaks: snap.peaks,
        armed: snap.armed,
      });
    }
    // Re-sync _armedTrackId from the rehydrated tracks. Without this,
    // getArmedTrack() returns null even when a track's `armed` flag is
    // true (single-arm invariant gets out of sync after softReset).
    const armedAfter = this.tracks.find((t) => t.armed);
    this._armedTrackId = armedAfter?.id ?? null;

    // 12) Reset transient state
    this.recording = false;
    this.countingIn = false;
    this._recordingTrackId = null;
    this.recordingStartedAt = 0;
    this.captureSampleRate = 0;
    this.lastCaptureDiagnostic = null;
    this.lastMicOpenInfo = null;
    this._playheadMs = 0;
    this._playheadStartedAt = null;

    // 13) Notify subscribers — listener Sets survive softReset on purpose
    this._notifyPlayhead();
  }

  /**
   * Schedule a brief silent buffer at the next audio quantum to "prime" the
   * iOS audio output session. After getUserMedia leaves the session in
   * PlayAndRecord mode, the destination occasionally stays silent on iOS
   * unless the graph is exercised first. This is a known WebKit quirk; the
   * trick is widely used in iOS Web Audio code.
   */
  private primeAudioOutput(): void {
    try {
      const buf = this.ctx.createBuffer(1, 256, this.ctx.sampleRate);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const gain = this.ctx.createGain();
      gain.gain.value = 0; // silent
      src.connect(gain).connect(this.ctx.destination);
      src.start(this.ctx.currentTime);
    } catch {
      /* ignore */
    }
  }

  get isRecording(): boolean {
    return this.recording;
  }

  get isPlaying(): boolean {
    return this._playbackActive;
  }

  get recordingElapsedMs(): number {
    return this.isRecording ? performance.now() - this.recordingStartedAt : 0;
  }

  get transportState(): TransportState {
    if (this.isRecording || this.countingIn) return 'recording';
    if (this.isPlaying) return 'playing';
    return 'idle';
  }

  get playheadMs(): number {
    return this._playheadMs;
  }

  /** Current playhead in ms, accounting for in-flight playback (live ticking value). */
  getLivePlayheadMs(): number {
    if (this._playbackActive) {
      return Tone.getTransport().seconds * 1000;
    }
    if (this._playheadStartedAt == null || this.transportState === 'idle') {
      return this._playheadMs;
    }
    // Recording path (legacy): no Tone.Transport involvement
    return this._playheadMs + (this.ctx.currentTime - this._playheadStartedAt) * 1000;
  }

  /** Set playhead position when transport is idle. For seeking during playback, use seek(). */
  setPlayheadMs(ms: number): void {
    this._playheadMs = Math.max(0, Math.round(ms));
    this._playheadStartedAt = null;
    this._notifyPlayhead();
  }

  /**
   * Seek to a position. If transport is idle, just moves the playhead. If
   * playback is running, restarts all active sources from the new position
   * (DAW convention — playback continues without pausing). Ignored during
   * recording.
   */
  seek(ms: number): void {
    const target = Math.max(0, Math.round(ms));
    if (this.transportState === 'recording') return;
    if (this.transportState === 'idle') {
      this.setPlayheadMs(target);
      return;
    }
    // Playing — stop, move playhead, restart from new position. Suppress
    // the onEnd callback during this internal stop so we don't bounce the
    // UI through an idle state.
    const cb = this.playbackOnEnd;
    this.playbackOnEnd = null;
    this.stopPlayback();
    this._playheadMs = target;
    if (cb) void this.play(cb);
  }

  onPlayhead(cb: (ms: number, state: TransportState) => void): () => void {
    this._playheadListeners.add(cb);
    return () => { this._playheadListeners.delete(cb); };
  }

  private _notifyPlayhead(): void {
    const ms = this.getLivePlayheadMs();
    const state = this.transportState;
    for (const cb of this._playheadListeners) cb(ms, state);
  }

  /** Subscribe to mic-open events. Fires when getUserMedia resolves and
   *  the engine has snapshotted the resulting MediaStreamTrack settings.
   *  Returns an unsubscribe function. Production main.ts no longer
   *  subscribes (the diagnostic panel that used it is gone), kept on
   *  the engine for future debug hooks. */
  onMicOpen(cb: (info: MicOpenInfo) => void): () => void {
    this._micOpenListeners.add(cb);
    return () => { this._micOpenListeners.delete(cb); };
  }

  /** Snapshot MediaStreamTrack settings, store on the engine, fire
   *  listeners. iOS leaves most fields undefined — that scarcity is the
   *  point: the UI surfaces whatever it DID return so we can correlate
   *  with audio quality. */
  private captureMicOpenInfo(): void {
    const track = this.mediaStream?.getAudioTracks()[0];
    const s = (track?.getSettings?.() ?? {}) as MediaTrackSettings & {
      latency?: number;
    };
    const info: MicOpenInfo = {
      deviceId: s.deviceId ?? null,
      label: track?.label || null,
      groupId: s.groupId ?? null,
      sampleRate: typeof s.sampleRate === 'number' ? s.sampleRate : null,
      channelCount: typeof s.channelCount === 'number' ? s.channelCount : null,
      latencySec: typeof s.latency === 'number' ? s.latency : null,
      echoCancellation: typeof s.echoCancellation === 'boolean' ? s.echoCancellation : null,
      noiseSuppression: typeof s.noiseSuppression === 'boolean' ? s.noiseSuppression : null,
      autoGainControl: typeof s.autoGainControl === 'boolean' ? s.autoGainControl : null,
    };
    this.lastMicOpenInfo = info;
    console.log('mic open:', info);
    for (const cb of this._micOpenListeners) {
      try { cb(info); } catch (e) { console.warn('mic-open listener threw:', e); }
    }
  }

  /** Max of (startAtMs + offsetMs + duration) over all tracks; floor of 30 s for empty sketches. */
  get sketchDurationMs(): number {
    let max = 0;
    for (const t of this.tracks) {
      if (isEmptyTrack(t)) continue;
      const trackDurMs = typeof t.recordingMs === 'number'
        ? t.recordingMs
        : t.buffer.duration * 1000;
      const end = t.startAtMs + t.offsetMs + trackDurMs;
      if (end > max) max = end;
    }
    return Math.max(30_000, Math.ceil(max));
  }

  /**
   * If the given track is the live recording target, returns peaks computed
   * from the in-flight `capturedChunks` plus the running duration. Returns
   * null otherwise. Used by the UI to repaint the lane in real time.
   */
  getRecordingPeaks(trackId: string, buckets: number): { peaks: number[]; durationMs: number } | null {
    if (this._recordingTrackId !== trackId) return null;
    let totalSamples = 0;
    for (const c of this.capturedChunks) totalSamples += c.length;
    if (totalSamples === 0) {
      return { peaks: new Array<number>(buckets * 2).fill(0), durationMs: 0 };
    }
    const samplesPerBucket = Math.max(1, Math.ceil(totalSamples / buckets));
    const peaks = new Array<number>(buckets * 2);
    for (let i = 0; i < buckets; i++) {
      peaks[i * 2] = 1;
      peaks[i * 2 + 1] = -1;
    }
    let globalIdx = 0;
    for (const chunk of this.capturedChunks) {
      for (let i = 0; i < chunk.length; i++) {
        const b = Math.min(buckets - 1, Math.floor(globalIdx / samplesPerBucket));
        const v = chunk[i];
        if (v < peaks[b * 2]) peaks[b * 2] = v;
        if (v > peaks[b * 2 + 1]) peaks[b * 2 + 1] = v;
        globalIdx++;
      }
    }
    for (let i = 0; i < buckets; i++) {
      if (peaks[i * 2] > peaks[i * 2 + 1]) {
        peaks[i * 2] = 0;
        peaks[i * 2 + 1] = 0;
      }
    }
    return { peaks, durationMs: (totalSamples / this.ctx.sampleRate) * 1000 };
  }

  /**
   * Play all unmuted tracks (with per-track offsets) without recording.
   * `onEnd` fires when the longest track finishes naturally OR when
   * stopPlayback() is called manually. Returns immediately after scheduling.
   *
   * Uses Tone.Transport as the timeline: each track's Player is synced
   * to the Transport and scheduled at its absolute song-position. Negative
   * offsets (clip pulled before song-start) are handled by seeking into
   * the buffer instead of negative time.
   */
  async play(onEnd: () => void): Promise<void> {
    if (this.isPlaying || this.isRecording) return;
    await this.ensureRunning();

    const active = this.tracks.filter((t) => !t.muted && !isEmptyTrack(t));
    if (active.length === 0) return;

    this.primeAudioOutput();

    const playheadSec = this._playheadMs / 1000;
    const transport = Tone.getTransport();

    // Reset Transport to the current playhead and clear prior schedule.
    transport.stop();
    transport.cancel(0);
    transport.seconds = playheadSec;

    // Schedule each track via its Player. computeStartInTransport returns
    // null if the clip is entirely behind the playhead.
    let maxEndSec = playheadSec;
    this._syncedPlayers = [];
    for (const t of active) {
      const sched = computeStartInTransport(t, playheadSec);
      if (!sched) continue;
      const tp = this.ensureTrackPlayer(t);
      try {
        tp.player.unsync();           // drop any prior sync (defensive)
        tp.player.stop();
      } catch { /* ignore */ }
      tp.player.sync();
      // Synced Player.start(time, offset) — `time` is interpreted as a
      // Transport position (seconds), `offset` is seek-into-buffer.
      tp.player.start(sched.transportTimeSec, sched.bufferOffsetSec);
      this._syncedPlayers.push(tp.player);
      const trackEnd = sched.transportTimeSec + (t.buffer.duration - sched.bufferOffsetSec);
      if (trackEnd > maxEndSec) maxEndSec = trackEnd;
    }

    if (this._syncedPlayers.length === 0) {
      // Nothing schedulable from this playhead position — bail without entering 'playing'.
      return;
    }

    this.playbackOnEnd = onEnd;
    this._playbackActive = true;
    // Schedule the auto-stop event slightly after the longest track ends.
    this._playbackEndEventId = transport.scheduleOnce(() => {
      this.stopPlayback();
    }, maxEndSec + 0.05);

    // Start Transport 150 ms ahead — gives iOS time to settle after a
    // PlayAndRecord → Playback session transition.
    transport.start('+0.15', playheadSec);
    this._notifyPlayhead();
  }

  stopPlayback(): void {
    if (!this._playbackActive) return;
    const transport = Tone.getTransport();
    if (this._playbackEndEventId !== null) {
      try { transport.clear(this._playbackEndEventId); } catch { /* ignore */ }
      this._playbackEndEventId = null;
    }
    // Snapshot live position so the playhead stays where we stopped (pause semantics).
    const live = this.getLivePlayheadMs();

    transport.stop();
    transport.cancel(0);
    for (const player of this._syncedPlayers) {
      try { player.unsync(); player.stop(); } catch { /* ignore */ }
    }
    this._syncedPlayers = [];

    this._playbackActive = false;
    this._playheadStartedAt = null;
    this._playheadMs = Math.max(0, live);
    this._notifyPlayhead();
    const cb = this.playbackOnEnd;
    this.playbackOnEnd = null;
    if (cb) cb();
  }

  /**
   * Start recording. If there are existing unmuted tracks, they begin
   * playing back at the same scheduled start time so the user can overdub.
   *
   * When count-in is enabled, this function returns immediately after
   * scheduling 4 click beats — `isCountingIn` will be true. The actual
   * MediaRecorder.start() and existing-track playback both happen on the
   * 5th beat. Callers can pass an `onRecordStart` callback to react to
   * the transition.
   */
  /** Lazily register the AudioWorklet module the first time we record. */
  private async ensureWorkletLoaded(): Promise<void> {
    if (this.workletLoaded) return;
    await this.ctx.audioWorklet.addModule(WORKLET_URL);
    this.workletLoaded = true;
  }

  /** Monotonic per-page take counter — first record press = 1. Used by
   *  debug-log so we can correlate "first take fades" with the numeric
   *  evidence. Never decremented; survives stopRecording/cancellation. */
  private _takeCount = 0;

  async startRecording(opts?: { onRecordStart?: () => void }): Promise<void> {
    if (this.isRecording || this.isCountingIn) throw new Error('already recording');
    if (this.isPlaying) this.stopPlayback();
    this._takeCount++;
    const takeNo = this._takeCount;
    const t0 = performance.now();
    debugLog('rec:start', {
      take: takeNo,
      ctxState: this.ctx.state,
      ctxRate: this.ctx.sampleRate,
      ctxTime: this.ctx.currentTime,
      countIn: this.countInEnabled,
      metroDuringRec: this.metronomeDuringRec,
      ua: navigator.userAgent,
    });
    await this.ensureRunning();
    await this.ensureWorkletLoaded();

    // Set up the WebRTC loopback BEFORE getUserMedia. iOS WebKit picks
    // the AudioSession mode at the moment of getUserMedia: with no
    // active WebRTC stream it goes to .voiceChat (engages Voice
    // Processing IO — adaptive noise suppression that progressively
    // strips sustained tones like guitar after the first ~1 s, even
    // with `noiseSuppression: false` in the constraints). With an
    // active WebRTC video-classified stream already running, iOS picks
    // .videoChat instead, which leaves the mic input unprocessed.
    //
    // This is also why overdubs sounded clean while the first take
    // didn't: subsequent takes inherit a loopback created during a
    // prior take. By bringing it up unconditionally before the mic
    // request, every take gets the same clean path.
    //
    // Also: pipe a short silent buffer through the loopback so the
    // RTCPeerConnection has actual audio activity to classify, then
    // route the output bus into it so backing tracks (if any) flow
    // through the same path during the take.
    const recordOutNode = await this.setupRecordOutput();
    // 220 Hz tone above WebRTC's HPF + Opus VAD thresholds so NetEq's
    // jitter buffer stays at steady state for the whole take (see
    // recordOutKeepAlive field comment). Stopped in teardownRecordOutput.
    const keepAlive = this.ctx.createOscillator();
    keepAlive.type = 'sine';
    keepAlive.frequency.value = 220;
    const keepAliveGain = this.ctx.createGain();
    keepAliveGain.gain.value = 5e-4;
    keepAlive.connect(keepAliveGain).connect(recordOutNode);
    keepAlive.start();
    this.recordOutKeepAlive = keepAlive;
    this.recordOutKeepAliveGain = keepAliveGain;
    // Give iOS one event-loop tick (and a short timer) to register the
    // WebRTC stream as active before we ask for the mic.
    await new Promise<void>((r) => setTimeout(r, 50));

    // Prefer the built-in mic via deviceId so playback can stay on
    // AirPods while capture uses the full-bandwidth iPhone mic. Falls
    // back to the BT-safe 16 kHz constraints when only BT inputs are
    // enumerable (also on the first record press, before labels are
    // populated — which then primes the device list for next time).
    const constraints = await pickMicConstraints();
    debugLog('rec:gum-call', { take: takeNo, constraints: JSON.stringify(constraints), tElapsed: Math.round(performance.now() - t0) });
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
    } catch (e) {
      // The built-in deviceId can become stale across BT routing
      // changes — drop the cache and try the BT-safe fallback once.
      console.warn('getUserMedia failed with preferred constraints, retrying BT-safe:', e);
      debugLog('rec:gum-retry', { take: takeNo, err: (e as Error).message });
      _cachedBuiltInDeviceId = null;
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: BT_FALLBACK_CONSTRAINTS });
    }
    const settings = this.mediaStream.getAudioTracks()[0]?.getSettings?.() ?? {};
    debugLog('rec:gum-resolved', {
      take: takeNo,
      tElapsed: Math.round(performance.now() - t0),
      label: this.mediaStream.getAudioTracks()[0]?.label ?? null,
      settings: JSON.stringify(settings),
    });
    // keepAlive stays running for the duration of recording — torn
    // down in teardownRecordOutput so NetEq never sees the dest go
    // silent between getUserMedia and routeOutputBus.
    // Snapshot what iOS gave us at mic open. Kept for debugging only;
    // not displayed in production UI.
    this.captureMicOpenInfo();

    // Build the capture graph: mic → GainNode (boost) → AudioWorkletNode.
    // The capture-time gain boosts the mic level before it lands in the
    // recorded buffer. autoGainControl is off (to preserve dynamics for
    // overdubbing), so the raw stream is often quiet on phone mics — we
    // bake a fixed pre-amp here so recordings hit a useful level. Output
    // gain on each track stays at unity (1.0) so the level slider is
    // intuitive after the fact.
    this.streamSource = this.ctx.createMediaStreamSource(this.mediaStream);
    const captureGain = this.ctx.createGain();
    captureGain.gain.value = 2.5;
    this.workletNode = new AudioWorkletNode(this.ctx, 'skizzen-recorder', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    });
    this.capturedChunks = [];
    // beginRecorderNow runs on the main thread the moment the worklet
    // confirms (via 'started') it has actually begun capturing. With
    // count-in, the worklet self-gates on `currentTime >= startAt` so the
    // transition is on the AudioContext clock, not setTimeout.
    const beginRecorderNow = () => {
      // Race guard: if the user cancelled during count-in, a late 'started'
      // can still be in flight from the worklet. Don't flip state for a
      // session that was already torn down.
      if (this._recordingTrackId === null) return;
      this.countingIn = false;
      this.recordingStartedAt = performance.now();
      this.captureSampleRate = this.ctx.sampleRate;
      this.recording = true;
      this._notifyPlayhead();
      opts?.onRecordStart?.();
    };
    let chunkIdx = 0;
    this.workletNode.port.onmessage = (e) => {
      const data = e.data;
      if (data instanceof Float32Array) {
        this.capturedChunks.push(data);
        // Per-chunk RMS + peak — this is the smoking gun. If the fade
        // is in iOS's audio path (VPIO, BT codec, whatever), we'll see
        // rms decay over the chunk index on take 1 but stay flat on
        // take 2. If both takes look identical here, the fade is
        // somewhere AFTER capture (playback path, encoder, etc.).
        let sumSq = 0;
        let peak = 0;
        for (let i = 0; i < data.length; i++) {
          const v = data[i];
          sumSq += v * v;
          const av = v >= 0 ? v : -v;
          if (av > peak) peak = av;
        }
        const rms = Math.sqrt(sumSq / data.length);
        debugLog('rec:chunk', {
          take: takeNo,
          i: chunkIdx,
          n: data.length,
          rms: Number(rms.toFixed(5)),
          peak: Number(peak.toFixed(5)),
        });
        chunkIdx++;
      } else if (data && typeof data === 'object') {
        if (data.type === 'done') {
          const resolve = this.workletStopResolve;
          this.workletStopResolve = null;
          resolve?.();
        } else if (data.type === 'started') {
          debugLog('rec:worklet-started', { take: takeNo, ctxTime: this.ctx.currentTime });
          beginRecorderNow();
        }
      }
    };
    this.streamSource.connect(captureGain).connect(this.workletNode);

    // No warmup needed in the 16 kHz capture path (see WARMUP_SEC at
    // module top). 50 ms in the count-in case purely for AudioContext
    // scheduling room before the first click.
    const warmupSec = this.countInEnabled ? 0.05 : WARMUP_SEC;
    const baseStart = this.ctx.currentTime + warmupSec;
    let recorderStartTime = baseStart;
    let countInDurationMs = 0;

    if (this.countInEnabled) {
      const bpm = this.metronome.getBpm();
      const secPerBeat = 60 / bpm;
      for (let i = 0; i < 4; i++) {
        this.metronome.scheduleClickAt(baseStart + i * secPerBeat, i === 0);
      }
      recorderStartTime = baseStart + 4 * secPerBeat;
      countInDurationMs = 4 * secPerBeat * 1000;
      this.countingIn = true;
    }

    // If "click through recording" is on, start the continuous metronome
    // loop at the same beat the recorder starts on (count-in already
    // covers beats 1-4 via scheduleClickAt; this picks up at beat 5).
    if (this.metronomeDuringRec) {
      this.metronome.startAt(recorderStartTime);
    }

    // Snapshot the playhead BEFORE count-in: this is where the new clip will live.
    this._recordedStartAtMs = this._playheadMs;
    const playheadAt = this._playheadMs;

    // Target track for this take. If a track is armed, record onto it —
    // either filling the empty slot or replacing its existing audio. Otherwise
    // create a fresh placeholder (legacy behavior, preserved for safety).
    let placeholderId: string;
    const armed = this.getArmedTrack();
    if (armed) {
      placeholderId = armed.id;
      // Reset the armed track to the "live recording" state. Keep its
      // user-customised gain/pan/muted/name; rewrite its position to the
      // current playhead so the take lands where the user is listening.
      armed.buffer = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
      armed.durationMs = 0;
      armed.startAtMs = this._recordedStartAtMs;
      // offsetMs stays at 0 — latency comp is baked into the buffer at
      // stopRecording (trimmed start), avoiding the iOS Safari
      // AudioBufferSourceNode.start(t, offset) pitch-artifact bug.
      armed.offsetMs = 0;
      armed.originalBlob = null;
      armed.peaks = [];
      armed.recordingMs = 0;
    } else {
      placeholderId = cryptoRandomId();
      const oneSampleBuffer = this.ctx.createBuffer(1, 1, this.ctx.sampleRate);
      const placeholder: Track = {
        id: placeholderId,
        name: `track ${this.tracks.length + 1}`,
        buffer: oneSampleBuffer,
        durationMs: 0,
        gain: 1.0,
        pan: 0.0,
        muted: false,
        offsetMs: 0,
        startAtMs: this._recordedStartAtMs,
        originalBlob: null,
        peaks: [],
        recordingMs: 0,
        armed: false,
      };
      this.tracks.push(placeholder);
      // Auto-arm: the now-recording track effectively IS armed.
      this.armTrack(placeholderId);
    }
    this._recordingTrackId = placeholderId;

    // WebRTC loopback was already established up-top (before
    // getUserMedia) so iOS's AudioSession is in .videoChat mode and
    // VPIO didn't engage. Now route the output bus through it so
    // backing tracks play out the speakerphone instead of the earpiece.
    this.routeOutputBus(recordOutNode);
    if (this.overdubBoost !== 1) {
      this.applyOverdubBoost(true, placeholderId);
    }

    // Schedule backing tracks via direct AudioContext-time Player.start —
    // NOT Tone.Transport.sync(). The Transport-relative approach worked
    // for standalone play() but caused the player start events to be
    // swallowed during recording on iOS. Direct scheduling matches what
    // the pre-Tone engine did and is what we already do for the
    // recorderStartTime hand-off to the worklet.
    this._syncedPlayers = [];
    for (const t of this.tracks) {
      if (t.id === placeholderId || t.muted || isEmptyTrack(t)) continue;
      const trackStartSec = (t.startAtMs + t.offsetMs) / 1000;
      const playheadSec = playheadAt / 1000;
      const relSec = trackStartSec - playheadSec;
      const startTime = recorderStartTime + Math.max(0, relSec);
      const bufferOffsetSec = relSec < 0 ? -relSec : 0;
      if (bufferOffsetSec >= t.buffer.duration) continue;
      const tp = this.ensureTrackPlayer(t);
      try { tp.player.unsync(); tp.player.stop(); } catch { /* ignore */ }
      tp.player.start(startTime, bufferOffsetSec);
      this._syncedPlayers.push(tp.player);
    }

    // Playhead clock — keep the legacy field too, so getLivePlayheadMs has a
    // valid fallback if Transport state is briefly out-of-sync on iOS.
    this._playheadStartedAt = recorderStartTime;
    this._notifyPlayhead();

    // Hand the worklet the exact AudioContext time to begin capturing.
    // The worklet self-gates on `currentTime >= startAt` and posts back
    // 'started' — which fires beginRecorderNow on the main thread.
    // No setTimeout, no wall-clock drift, no leak from count-in clicks
    // or BT-codec-warmup audio.
    this.workletNode.port.postMessage({ type: 'start_at', time: recorderStartTime });
  }

  /**
   * Stop recording, decode the captured audio into a new Track, append it
   * to the list, and return it.
   */
  /**
   * Tear down the worklet capture graph + release the mic. Used by
   * stopRecording and cancelCountIn.
   */
  private teardownCapture(): void {
    if (this.workletNode) {
      try {
        this.workletNode.disconnect();
      } catch {
        /* ignore */
      }
      this.workletNode = null;
    }
    if (this.streamSource) {
      try {
        this.streamSource.disconnect();
      } catch {
        /* ignore */
      }
      this.streamSource = null;
    }
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
  }

  /**
   * Build a recording-time output path that bypasses iOS PlayAndRecord's
   * earpiece routing. The backing-track graph connects into a
   * MediaStreamAudioDestinationNode; that stream is then sent through a
   * local WebRTC peer-connection loopback into a hidden <audio> element.
   *
   * Why WebRTC and not a plain <audio> srcObject = stream? Because while
   * getUserMedia is active, iOS Safari applies PlayAndRecord routing to
   * every page-wide audio output (Web Audio AND ordinary <audio>
   * elements) — they all go to the earpiece. The one exception is WebRTC
   * audio, which Safari classifies as voice/video chat and routes through
   * the speakerphone (main loudspeaker) by default. Wrapping our backing
   * tracks in a peer-connection loop is the only known JS workaround
   * (there is no JS equivalent to AVAudioSession.overrideOutputAudioPort).
   *
   * Returns the dest AudioNode the caller connects backing tracks into.
   */
  private async setupRecordOutput(): Promise<AudioNode> {
    this.teardownRecordOutput();

    const dest = this.ctx.createMediaStreamDestination();

    // Use a <video> element instead of <audio>. On iOS, <video> media-
    // elements sometimes get different audio routing than <audio>; pairing
    // it with the WebRTC loopback is our last-ditch attempt to stay on the
    // main loudspeaker during PlayAndRecord. No video data flows through
    // this — it's just an audio sink with a different element nameplate.
    const el = document.createElement('video');
    el.autoplay = true;
    el.muted = false;
    el.setAttribute('playsinline', '');
    el.setAttribute('webkit-playsinline', '');
    el.style.position = 'absolute';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.opacity = '0';
    el.style.pointerEvents = 'none';
    document.body.appendChild(el);

    const pc1 = new RTCPeerConnection();
    const pc2 = new RTCPeerConnection();
    pc1.onicecandidate = (e) => {
      if (e.candidate) pc2.addIceCandidate(e.candidate).catch(() => {});
    };
    pc2.onicecandidate = (e) => {
      if (e.candidate) pc1.addIceCandidate(e.candidate).catch(() => {});
    };
    pc2.ontrack = (e) => {
      el.srcObject = e.streams[0] ?? new MediaStream([e.track]);
      el.play().catch((err) => console.warn('record-output <video> play rejected:', err));
    };

    for (const track of dest.stream.getTracks()) {
      pc1.addTrack(track, dest.stream);
    }

    // SDP munge: disable Opus DTX (discontinuous transmission). Without
    // this, Opus stops sending packets when input level falls below its
    // VAD threshold (~-55 dBFS) — which our keepAlive ConstantSource
    // sits well below. The receiver's NetEq then sees a gap, fills it
    // with comfort noise, and time-compresses (= pitch UP) once real
    // audio arrives to catch its buffer back up. usedtx=0 forces Opus
    // to always send real packets so NetEq's buffer stays stable.
    const offer = await pc1.createOffer();
    if (offer.sdp) offer.sdp = patchOpusUsedtxOff(offer.sdp);
    await pc1.setLocalDescription(offer);
    await pc2.setRemoteDescription(offer);
    const answer = await pc2.createAnswer();
    if (answer.sdp) answer.sdp = patchOpusUsedtxOff(answer.sdp);
    await pc2.setLocalDescription(answer);
    await pc1.setRemoteDescription(answer);

    this.recordOutDest = dest;
    this.recordOutAudio = el;
    this.recordOutPc1 = pc1;
    this.recordOutPc2 = pc2;
    return dest;
  }

  private teardownRecordOutput(): void {
    if (this.recordOutKeepAlive) {
      try {
        this.recordOutKeepAlive.stop();
        this.recordOutKeepAlive.disconnect();
      } catch {
        /* ignore — already stopped */
      }
      this.recordOutKeepAlive = null;
    }
    if (this.recordOutKeepAliveGain) {
      try { this.recordOutKeepAliveGain.disconnect(); } catch { /* ignore */ }
      this.recordOutKeepAliveGain = null;
    }
    if (this.recordOutAudio) {
      try {
        this.recordOutAudio.pause();
        this.recordOutAudio.srcObject = null;
        this.recordOutAudio.remove();
      } catch {
        /* ignore */
      }
      this.recordOutAudio = null;
    }
    if (this.recordOutPc1) {
      try {
        this.recordOutPc1.close();
      } catch {
        /* ignore */
      }
      this.recordOutPc1 = null;
    }
    if (this.recordOutPc2) {
      try {
        this.recordOutPc2.close();
      } catch {
        /* ignore */
      }
      this.recordOutPc2 = null;
    }
    if (this.recordOutDest) {
      try {
        this.recordOutDest.disconnect();
      } catch {
        /* ignore */
      }
      this.recordOutDest = null;
    }
  }

  /**
   * After a getUserMedia recording cycle, iOS leaves the audio session in
   * PlayAndRecord mode, which silently attenuates Web Audio output by ~3-6 dB
   * relative to the Playback mode you get on a fresh page load. To force the
   * session back to Playback we:
   *   1) pause and re-play the silent keep-alive <audio> element (iOS uses
   *      the active media element as a session-category signal)
   *   2) suspend and resume the AudioContext (forces session re-evaluation)
   * Fire-and-forget; failures are logged but don't block recording cleanup.
   */
  private async resetAudioSessionAfterRecord(): Promise<void> {
    const el = this.keepAliveAudioEl;
    try {
      if (el && !el.paused) {
        try { el.pause(); } catch { /* ignore */ }
      }
      try { await this.ctx.suspend(); } catch { /* ignore */ }
      try { await this.ctx.resume(); } catch { /* ignore */ }
      if (el) {
        const p = el.play();
        if (p && typeof p.then === 'function') {
          await p.catch((err) => {
            console.warn('keep-alive resume after record failed:', err);
          });
        }
      }
    } catch (e) {
      console.warn('audio session reset failed:', e);
    }
  }

  /**
   * If a recording is mid-count-in (no audio captured yet), abort cleanly
   * without producing a Track. Returns true if it cancelled.
   */
  cancelCountIn(): boolean {
    if (!this.countingIn) return false;
    try {
      this.workletNode?.port.postMessage('cancel_start');
    } catch {
      /* ignore — worklet may already be torn down */
    }
    this.countingIn = false;
    // Stop any in-flight backing-track players.
    this._teardownTransportPlayback();
    // Revert overdub-boost + route the output bus back to Tone.Destination.
    if (this.overdubBoost !== 1) this.applyOverdubBoost(false);
    this.routeOutputBus(null);
    this.metronome.stop();
    this.teardownCapture();
    this.teardownRecordOutput();
    this.capturedChunks = [];
    // Leave the (possibly-armed) target track in place — it's either
    // a pre-existing empty armed track or a freshly-created placeholder.
    // Reset its live-recording marker so it reads as "empty" again.
    if (this._recordingTrackId) {
      const t = this.tracks.find((t) => t.id === this._recordingTrackId);
      if (t) {
        t.recordingMs = undefined;
        t.durationMs = 0;
      }
      this._recordingTrackId = null;
    }
    this._playheadStartedAt = null;
    void this.resetAudioSessionAfterRecord();
    // After cancel: release keep-alive promptly (no recording happened,
    // no risk of muting fresh audio output). 1.5 s gives iOS time to
    // tear down PlayAndRecord cleanly.
    this.scheduleKeepAliveRelease(1500);
    this._notifyPlayhead();
    return true;
  }

  async stopRecording(name?: string): Promise<Track> {
    if (!this.recording) throw new Error('not recording');

    const stopTakeNo = this._takeCount;
    debugLog('rec:stop-begin', { take: stopTakeNo });

    // Tell the worklet to stop and wait for the 'done' message that
    // confirms a final flush has been queued back to us. There's at most
    // a one-quantum (~3ms) round-trip via the port — fast.
    const stopped = new Promise<void>((resolve) => {
      this.workletStopResolve = resolve;
    });
    this.workletNode?.port.postMessage('stop');
    await stopped;

    // Snapshot live playhead while transport is still 'recording' (the getter
    // depends on the live clock; once we flip recording=false it freezes).
    const livePlayheadMs = this.getLivePlayheadMs();

    this.recording = false;

    // Halt backing-track playback. Revert overdub-boost + bus routing.
    this._teardownTransportPlayback();
    if (this.overdubBoost !== 1) this.applyOverdubBoost(false);
    this.routeOutputBus(null);
    this.metronome.stop();

    this.teardownCapture();
    this.teardownRecordOutput();
    // Force iOS audio session back to Playback mode (otherwise post-record
    // playback is several dB quieter than a fresh page load). Async, but we
    // don't await — buffer decoding can run in parallel.
    void this.resetAudioSessionAfterRecord();
    // After successful recording: release keep-alive after 3 s so iOS
    // can drop PlayAndRecord and connected Bluetooth headphones can
    // switch back to A2DP (high-quality output). The user can press
    // play/+/record again within that window and we'll cancel the
    // release via ensureRunning().
    this.scheduleKeepAliveRelease(3000);

    // Snap playhead to where recording stopped, freeze the clock
    this._playheadMs = Math.max(0, livePlayheadMs);
    this._playheadStartedAt = null;

    // Concatenate captured Float32 chunks into a single AudioBuffer at
    // the AudioContext's native sample rate — no decode/resample step,
    // captured samples ARE the buffer.
    const totalLength = this.capturedChunks.reduce((sum, c) => sum + c.length, 0);
    if (totalLength === 0) {
      throw new Error('no audio captured');
    }
    // Diagnostic: log captured-sample-count vs wall-clock elapsed time vs
    // declared context sample rate. If iOS shifted the context's effective
    // sample rate mid-recording (Bluetooth profile switch), captured /
    // elapsedSec will diverge from ctx.sampleRate, and the resulting WAV
    // will play back at the wrong pitch.
    const elapsedSec =
      (performance.now() - this.recordingStartedAt) / 1000;
    const effectiveRate = elapsedSec > 0 ? totalLength / elapsedSec : 0;
    const audioTrack = this.mediaStream?.getAudioTracks()[0];
    const streamSettings = audioTrack?.getSettings?.();
    const startRate = this.captureSampleRate || this.ctx.sampleRate;
    const stopRate = this.ctx.sampleRate;
    if (startRate !== stopRate) {
      console.warn('CONTEXT SAMPLE RATE SHIFTED MID-RECORDING', {
        startRate, stopRate, effectiveRate, totalLength,
      });
    }
    if (Math.abs(effectiveRate - startRate) > startRate * 0.05) {
      console.warn('CAPTURE RATE MISMATCH', { startRate, effectiveRate });
    }
    // Use the START rate, not the current rate — the worklet captured at
    // the START rate. Declaring a buffer at a different rate would cause
    // the playback to be pitch-shifted by the ratio.
    const fullBuffer = this.ctx.createBuffer(1, totalLength, startRate);
    const fullData = fullBuffer.getChannelData(0);
    let writeOffset = 0;
    for (const chunk of this.capturedChunks) {
      fullData.set(chunk, writeOffset);
      writeOffset += chunk.length;
    }
    this.capturedChunks = [];

    // Latency compensation: drop the first N samples so the recorded
    // audio aligns with what the user thought they played. We bake the
    // trim into the buffer here rather than carrying it as offsetMs.
    // Why: AudioBufferSourceNode.start(time, offset) with a non-zero
    // offset triggers an iOS Safari bug that produces audible pitch
    // artifacts at the playback start point. Baking the trim into the
    // buffer means we always call start(time, 0) on the playback path.
    let buffer = fullBuffer;
    const trimMs = this.latencyOffsetMs;
    if (trimMs > 0) {
      const trimSamples = Math.min(
        Math.round((trimMs / 1000) * startRate),
        totalLength - 1,
      );
      const trimmedLength = totalLength - trimSamples;
      buffer = this.ctx.createBuffer(1, trimmedLength, startRate);
      buffer.getChannelData(0).set(fullData.subarray(trimSamples));
    }

    // Lightweight capture diagnostic kept on the engine for future
    // debugging. No longer surfaced to the UI — at 16 kHz exact iOS
    // delivers consistently clean audio, so per-take stats aren't worth
    // the noise. Stays in the console log for ad-hoc inspection.
    const diag = {
      ctxRate: stopRate,
      startRate,
      effectiveRate: Math.round(effectiveRate),
      streamRate: streamSettings?.sampleRate ?? '?',
      samples: totalLength,
      elapsedSec: elapsedSec.toFixed(2),
      streamLabel: audioTrack?.label || null,
      streamChannelCount:
        typeof streamSettings?.channelCount === 'number'
          ? streamSettings.channelCount
          : null,
    };
    console.log('capture diagnostic:', diag);
    this.lastCaptureDiagnostic = diag;
    debugLog('rec:stop-done', { take: stopTakeNo, ...diag });
    void flushDebugLogNow();

    // Encode as WAV for upload. The server stores this directly.
    // We upload the TRIMMED buffer so server-side mixing matches what
    // the user heard in-app (no offset/seek tricks needed downstream).
    const wavBlob = audioBufferToWav(buffer);

    // Replace the placeholder created in startRecording with the real take.
    // Falling back to push-as-new in the (impossible) case the placeholder
    // went missing keeps the code defensive.
    const placeholderIdx = this._recordingTrackId
      ? this.tracks.findIndex((t) => t.id === this._recordingTrackId)
      : -1;
    let finalised: Track;
    if (placeholderIdx >= 0) {
      const ph = this.tracks[placeholderIdx];
      finalised = {
        ...ph,
        name: name || ph.name,
        buffer,
        durationMs: Math.round(buffer.duration * 1000),
        originalBlob: wavBlob,
        peaks: computePeaks(buffer),
        recordingMs: undefined,
        // Latency comp is baked into the (trimmed) buffer above, so the
        // playback offset is 0 — avoids the iOS Safari pitch-warble bug.
        offsetMs: 0,
      };
      this.tracks[placeholderIdx] = finalised;
      // Refresh the Tone.Player's buffer (creating the wrapper if needed)
      this.setTrackBuffer(finalised, buffer);
    } else {
      finalised = {
        id: cryptoRandomId(),
        name: name || `track ${this.tracks.length + 1}`,
        buffer,
        durationMs: Math.round(buffer.duration * 1000),
        gain: 1.0,
        pan: 0.0,
        muted: false,
        offsetMs: 0,
        startAtMs: this._recordedStartAtMs,
        originalBlob: wavBlob,
        peaks: computePeaks(buffer),
        armed: false,
      };
      this.tracks.push(finalised);
      this.ensureTrackPlayer(finalised);
    }
    this._recordingTrackId = null;
    this._notifyPlayhead();
    return finalised;
  }

  /**
   * Add a track that was downloaded from the server (existing sketch reopen).
   * `serverId` is the DB row id — kept so future uploads can reference back
   * (not currently used; placeholder for delete flow).
   */
  async importTrack(args: {
    arrayBuffer: ArrayBuffer;
    name: string;
    durationMs?: number;
    offsetMs?: number;
    startAtMs?: number;
    gain?: number;
    pan?: number;
    muted?: boolean;
    serverId?: string;
  }): Promise<Track> {
    await this.ensureRunning();
    const buffer = await this.ctx.decodeAudioData(args.arrayBuffer.slice(0));
    const track: Track = {
      id: args.serverId || cryptoRandomId(),
      name: args.name,
      buffer,
      durationMs: args.durationMs ?? Math.round(buffer.duration * 1000),
      gain: typeof args.gain === 'number' ? Math.max(0, Math.min(2, args.gain)) : 1.0,
      pan: typeof args.pan === 'number' ? Math.max(-1, Math.min(1, args.pan)) : 0.0,
      muted: !!args.muted,
      offsetMs: args.offsetMs ?? 0,
      startAtMs: Math.max(0, Math.round(args.startAtMs ?? 0)),
      originalBlob: null,
      peaks: computePeaks(buffer),
      armed: false,
    };
    this.tracks.push(track);
    this.ensureTrackPlayer(track);
    return track;
  }

  removeTrack(id: string): void {
    const idx = this.tracks.findIndex((t) => t.id === id);
    if (idx >= 0) this.tracks.splice(idx, 1);
    if (this._armedTrackId === id) this._armedTrackId = null;
    const tp = this.trackPlayers.get(id);
    if (tp) {
      try { tp.player.dispose(); } catch { /* ignore */ }
      try { tp.channel.dispose(); } catch { /* ignore */ }
      this.trackPlayers.delete(id);
    }
  }

  setMuted(id: string, muted: boolean): void {
    const t = this.tracks.find((t) => t.id === id);
    if (!t) return;
    t.muted = muted;
    const tp = this.trackPlayers.get(id);
    if (tp) {
      try { tp.channel.mute = muted; } catch { /* ignore */ }
    }
  }

  setTrackOffsetMs(id: string, offsetMs: number): void {
    const t = this.tracks.find((t) => t.id === id);
    if (!t) return;
    // Wide clamp: offset is now the unified position control (replacing
    // the separate "start" stepper). The user can drag a clip up to a
    // minute before or after its recorded position. Anything past the
    // timeline start gets visually clipped by paintLaneCanvas; anything
    // past the end of a track is naturally inaudible.
    t.offsetMs = Math.max(-60000, Math.min(60000, Math.round(offsetMs)));
  }

  setTrackStartAtMs(id: string, value: number): void {
    const t = this.tracks.find((t) => t.id === id);
    if (!t) return;
    t.startAtMs = Math.max(0, Math.round(value));
    // No live-update of in-flight sources — caller stops + re-plays to hear changes.
  }

  setTrackGain(id: string, value: number): void {
    const t = this.tracks.find((t) => t.id === id);
    if (!t) return;
    const clamped = Math.max(0, Math.min(2, value));
    t.gain = clamped;
    const tp = this.trackPlayers.get(id);
    if (tp) {
      try { tp.channel.volume.rampTo(gainToDb(clamped), 0.01); } catch { /* ignore */ }
    }
  }

  setTrackPan(id: string, value: number): void {
    const t = this.tracks.find((t) => t.id === id);
    if (!t) return;
    const clamped = Math.max(-1, Math.min(1, value));
    t.pan = clamped;
    const tp = this.trackPlayers.get(id);
    if (tp) {
      try { tp.channel.pan.rampTo(clamped, 0.01); } catch { /* ignore */ }
    }
  }

  clearAll(): void {
    for (const tp of this.trackPlayers.values()) {
      try { tp.player.dispose(); } catch { /* ignore */ }
      try { tp.channel.dispose(); } catch { /* ignore */ }
    }
    this.trackPlayers.clear();
    this.tracks.length = 0;
    this._armedTrackId = null;
  }

  /**
   * Lazily create (or refresh) the Tone.Player + Tone.Channel for a track.
   * Returns the existing wrapper if one is already registered; otherwise
   * builds a new pair connected to Tone.Destination.
   *
   * Caller must ensure `t.buffer` has audible content; empty tracks
   * (durationMs === 0) get a 1-sample-silence Player which is fine — they
   * just have nothing to schedule.
   */
  private ensureTrackPlayer(t: Track): TrackPlayer {
    let tp = this.trackPlayers.get(t.id);
    if (tp) return tp;
    const player = new Tone.Player();
    try {
      player.buffer.set(t.buffer);
    } catch {
      /* ignore — empty track or test stub */
    }
    const channel = new Tone.Channel({
      volume: gainToDb(t.gain),
      pan: t.pan,
      mute: t.muted,
    });
    player.connect(channel);
    // Route via the shared output bus so we can re-route everyone at once
    // (Destination ↔ WebRTC loopback) without per-channel disconnects.
    if (this.outputBus) {
      channel.connect(this.outputBus);
    } else {
      channel.toDestination();
    }
    tp = { player, channel };
    this.trackPlayers.set(t.id, tp);
    return tp;
  }

  /**
   * Replace the AudioBuffer behind a track's Player without rebuilding the
   * Channel — used by stopRecording to swap the 1-sample placeholder for
   * the captured buffer. If no wrapper exists yet, creates one.
   */
  private setTrackBuffer(t: Track, buffer: AudioBuffer): void {
    let tp = this.trackPlayers.get(t.id);
    if (!tp) {
      tp = this.ensureTrackPlayer(t);
    }
    try {
      tp.player.buffer.set(buffer);
    } catch (e) {
      console.warn('failed to update player buffer', e);
    }
  }

  /**
   * Route every track's Channel either to a custom AudioNode (the WebRTC
   * loopback destination, during recording) or back to Tone.Destination
   * (playback / idle).
   *
   * Disconnects each Channel first so we don't double-connect.
   */
  private connectAllChannels(dest: AudioNode | null): void {
    for (const tp of this.trackPlayers.values()) {
      try { tp.channel.disconnect(); } catch { /* ignore */ }
      try {
        if (dest === null) tp.channel.toDestination();
        else tp.channel.connect(dest);
      } catch (e) {
        console.warn('channel route failed', e);
      }
    }
  }

  /**
   * Stop Tone.Transport, cancel scheduled events, and unsync every synced
   * Player. Safe to call when no playback is active.
   */
  private _teardownTransportPlayback(): void {
    const transport = Tone.getTransport();
    try { transport.stop(); transport.cancel(0); } catch { /* ignore */ }
    for (const player of this._syncedPlayers) {
      try { player.unsync(); player.stop(); } catch { /* ignore */ }
    }
    this._syncedPlayers = [];
  }

  /**
   * Apply (or revert) the iOS overdub-boost multiplier to all non-recording
   * track Channels. Bumps the channel volume by the engine's overdubBoost
   * factor while recording so the small earpiece speaker is audible.
   */
  private applyOverdubBoost(on: boolean, excludeTrackId: string | null = null): void {
    for (const t of this.tracks) {
      if (excludeTrackId && t.id === excludeTrackId) continue;
      const tp = this.trackPlayers.get(t.id);
      if (!tp) continue;
      const targetGain = on ? t.gain * this.overdubBoost : t.gain;
      try { tp.channel.volume.rampTo(gainToDb(targetGain), 0.01); } catch { /* ignore */ }
    }
  }

  get armedTrackId(): string | null {
    return this._armedTrackId;
  }

  getArmedTrack(): Track | null {
    if (!this._armedTrackId) return null;
    return this.tracks.find((t) => t.id === this._armedTrackId) ?? null;
  }

  /** Arm the given track id (or pass null to disarm). Enforces single-arm invariant. */
  armTrack(id: string | null): void {
    for (const t of this.tracks) {
      t.armed = id !== null && t.id === id;
    }
    this._armedTrackId = id;
  }

  /**
   * Append a new empty track and arm it. The track starts at the current
   * playhead, has no audio buffer of its own (placeholder 1-sample silence
   * to keep the schema simple), and remains in the list until either filled
   * by a recording or explicitly deleted.
   */
  addEmptyArmedTrack(): Track {
    const id = cryptoRandomId();
    const silentBuffer = this.ctx.createBuffer(1, 1, this.ctx.sampleRate || 48000);
    const t: Track = {
      id,
      name: `track ${this.tracks.length + 1}`,
      buffer: silentBuffer,
      durationMs: 0,
      gain: 1.0,
      pan: 0.0,
      muted: false,
      offsetMs: 0,
      startAtMs: this._playheadMs,
      originalBlob: null,
      peaks: [],
      armed: false,
    };
    this.tracks.push(t);
    this.armTrack(id);
    return t;
  }

}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Tiny fallback for very old runtimes
  return `t_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

